import { normalizeShape } from '../types.ts'
import type {
  Border, CanvasBackground, DesignCanvas, DesignDocument, Frame, ImageFilters, ImageFit, ImageSource, Layer,
  LayerConstraints, LayerMask, Shadow, Stroke, TextFont,
} from './types.ts'
import { DESIGN_SCHEMA_VERSION } from './types.ts'

export type ValidationError = { path: string; code: string; message: string }
export type ValidationResult = { ok: boolean; errors: ValidationError[] }

const LAYER_TYPES = new Set(['image', 'text', 'sticker', 'shape'])

function num(v: unknown, fallback = 0): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}
function clamp01(v: unknown, fallback = 0.5): number {
  const n = num(v, fallback)
  return Math.max(0, Math.min(1, n))
}
function str(v: unknown, fallback = ''): string {
  return v === undefined || v === null ? fallback : String(v)
}
function color(v: unknown, fallback: string): string {
  const s = str(v, '').trim()
  return /^(#[0-9a-f]{3,8}|rgba?\(.+\)|hsla?\(.+\)|[a-z]+)$/i.test(s) ? s : fallback
}
function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

function normalizeFrame(v: unknown): Frame {
  const f = isObj(v) ? v : {}
  return {
    xMm: num(f.xMm),
    yMm: num(f.yMm),
    wMm: Math.max(0, num(f.wMm)),
    hMm: Math.max(0, num(f.hMm)),
    rotationDeg: ((num(f.rotationDeg) % 360) + 360) % 360,
  }
}

function normalizeConstraints(v: unknown): LayerConstraints {
  const c = isObj(v) ? v : {}
  return {
    locked: !!c.locked,
    contentOnly: !!c.contentOnly,
    optional: c.optional === undefined ? true : !!c.optional,
    ...(c.role ? { role: str(c.role) } : {}),
  }
}

function normalizeFit(v: unknown): ImageFit {
  const f = isObj(v) ? v : {}
  return {
    mode: f.mode === 'contain' ? 'contain' : 'cover',
    offsetX: clamp01(f.offsetX),
    offsetY: clamp01(f.offsetY),
    scale: Math.max(1, Math.min(8, num(f.scale, 1) || 1)),
    ...(f.flipH ? { flipH: true } : {}),
    ...(f.flipV ? { flipV: true } : {}),
  }
}

function normalizeSource(v: unknown): ImageSource {
  const s = isObj(v) ? v : {}
  const dims = {
    ...(s.url ? { url: str(s.url) } : {}),
    ...(num(s.width) > 0 ? { width: Math.round(num(s.width)) } : {}),
    ...(num(s.height) > 0 ? { height: Math.round(num(s.height)) } : {}),
  }
  if (s.kind === 'upload' && str(s.uploadId)) return { kind: 'upload', uploadId: str(s.uploadId), ...dims }
  if (s.kind === 'asset' && str(s.assetId)) return { kind: 'asset', assetId: str(s.assetId), ...dims }
  return { kind: 'placeholder', ...(s.slotIndex !== undefined ? { slotIndex: Math.max(0, Math.round(num(s.slotIndex))) } : {}) }
}

function normalizeMask(v: unknown): LayerMask {
  const m = isObj(v) ? v : {}
  switch (m.kind) {
    case 'circle': return { kind: 'circle' }
    case 'rounded': return { kind: 'rounded', radiusMm: Math.max(0, num(m.radiusMm)) }
    case 'hexagon': return { kind: 'hexagon', orientation: m.orientation === 'pointy' ? 'pointy' : 'flat' }
    case 'asset': return str(m.assetId) ? { kind: 'asset', assetId: str(m.assetId), ...(str(m.path) ? { path: str(m.path) } : {}) } : { kind: 'none' }
    case 'svg': return str(m.path) ? { kind: 'svg', path: str(m.path) } : { kind: 'none' }
    default: return { kind: 'none' }
  }
}

function normalizeFilters(v: unknown): ImageFilters {
  const f = isObj(v) ? v : {}
  const pct = (x: unknown) => Math.max(-100, Math.min(100, num(x)))
  return {
    grayscale: !!f.grayscale,
    sepia: Math.max(0, Math.min(100, num(f.sepia))),
    brightness: pct(f.brightness),
    contrast: pct(f.contrast),
    saturation: pct(f.saturation),
  }
}

function normalizeBorder(v: unknown): Border {
  if (!isObj(v) || !(num(v.widthMm) > 0)) return null
  return { widthMm: num(v.widthMm), color: color(v.color, '#000000') }
}
function normalizeStroke(v: unknown): Stroke {
  return normalizeBorder(v)
}
function normalizeShadow(v: unknown): Shadow {
  if (!isObj(v)) return null
  return { xMm: num(v.xMm), yMm: num(v.yMm), blurMm: Math.max(0, num(v.blurMm)), color: color(v.color, 'rgba(0,0,0,0.35)') }
}

function normalizeFont(v: unknown): TextFont {
  const f = isObj(v) ? v : {}
  const weight = Math.round(num(f.weight, 400))
  return {
    familyId: str(f.familyId),
    ...(str(f.family) ? { family: str(f.family) } : {}),
    weight: Math.max(100, Math.min(900, weight || 400)),
    italic: !!f.italic,
    sizePt: Math.max(4, Math.min(400, num(f.sizePt, 24) || 24)),
    lineHeight: Math.max(0.6, Math.min(3, num(f.lineHeight, 1.2) || 1.2)),
    letterSpacingEm: Math.max(-0.2, Math.min(1, num(f.letterSpacingEm))),
  }
}

function normalizeBackground(v: unknown): CanvasBackground {
  const b = isObj(v) ? v : {}
  if (b.type === 'asset' && str(b.assetId)) return { type: 'asset', assetId: str(b.assetId), ...(str(b.url) ? { url: str(b.url) } : {}), tint: str(b.tint) || null }
  if (b.type === 'upload' && str(b.uploadId)) {
    return {
      type: 'upload',
      uploadId: str(b.uploadId),
      ...(str(b.url) ? { url: str(b.url) } : {}),
      ...(num(b.width) > 0 ? { width: Math.round(num(b.width)) } : {}),
      ...(num(b.height) > 0 ? { height: Math.round(num(b.height)) } : {}),
      fit: normalizeFit(b.fit),
    }
  }
  if (b.type === 'gradient' && Array.isArray(b.stops)) {
    return {
      type: 'gradient',
      angleDeg: num(b.angleDeg, 180),
      stops: b.stops.filter(isObj).map((s) => ({ offset: clamp01(s.offset, 0), color: color(s.color, '#FFFFFF') })),
    }
  }
  return { type: 'color', value: color(b.value, '#FFFFFF') }
}

export function normalizeCanvas(v: unknown): DesignCanvas {
  const c = isObj(v) ? v : {}
  const zones = Array.isArray(c.zones)
    ? c.zones.filter(isObj).map((z) => ({
        kind: (z.kind === 'handle' || z.kind === 'seam' ? z.kind : 'hidden') as 'hidden' | 'handle' | 'seam',
        xMm: num(z.xMm), yMm: num(z.yMm), wMm: Math.max(0, num(z.wMm)), hMm: Math.max(0, num(z.hMm)),
        ...(str(z.label) ? { label: str(z.label) } : {}),
      }))
    : []
  return {
    widthMm: Math.max(0, num(c.widthMm)),
    heightMm: Math.max(0, num(c.heightMm)),
    bleedMm: Math.max(0, num(c.bleedMm)),
    safeMarginMm: Math.max(0, num(c.safeMarginMm)),
    dpi: Math.max(72, Math.min(600, Math.round(num(c.dpi, 300)) || 300)),
    shape: normalizeShape(c.shape),
    cornerRadiusMm: Math.max(0, num(c.cornerRadiusMm)),
    shapeParams: isObj(c.shapeParams) && Object.keys(c.shapeParams).length ? (c.shapeParams as DesignCanvas['shapeParams']) : null,
    ...(zones.length ? { zones } : {}),
    background: normalizeBackground(c.background),
    colorProfile: 'sRGB',
  }
}

function normalizeLayerBase(l: Record<string, unknown>, fallbackId: string) {
  return {
    id: str(l.id) || fallbackId,
    ...(str(l.name) ? { name: str(l.name) } : {}),
    visible: l.visible === undefined ? true : !!l.visible,
    locked: !!l.locked,
    opacity: Math.max(0, Math.min(1, num(l.opacity, 1))),
    frame: normalizeFrame(l.frame),
    constraints: normalizeConstraints(l.constraints),
  }
}

export function normalizeLayer(v: unknown, index: number): Layer | null {
  if (!isObj(v)) return null
  const type = str(v.type)
  if (!LAYER_TYPES.has(type)) return null
  const base = normalizeLayerBase(v, `l${index + 1}`)
  if (type === 'image') {
    return {
      ...base, type: 'image',
      source: normalizeSource(v.source),
      fit: normalizeFit(v.fit),
      mask: normalizeMask(v.mask),
      filters: normalizeFilters(v.filters),
      border: normalizeBorder(v.border),
      shadow: normalizeShadow(v.shadow),
    }
  }
  if (type === 'text') {
    const align = (['left', 'center', 'right', 'justify'] as const).find((a) => a === v.align) ?? 'left'
    const valign = (['top', 'middle', 'bottom'] as const).find((a) => a === v.verticalAlign) ?? 'top'
    const fitted = num(v.fittedSizePt)
    return {
      ...base, type: 'text',
      text: str(v.text),
      font: normalizeFont(v.font),
      color: color(v.color, '#1A1A1A'),
      align, verticalAlign: valign,
      autoFit: v.autoFit === 'none' ? 'none' : 'shrink',
      ...(fitted > 0 ? { fittedSizePt: fitted } : {}),
      stroke: normalizeStroke(v.stroke),
      shadow: normalizeShadow(v.shadow),
    }
  }
  if (type === 'sticker') {
    const s = isObj(v.source) ? v.source : {}
    if (!str(s.assetId)) return null
    return {
      ...base, type: 'sticker',
      source: {
        assetId: str(s.assetId),
        ...(str(s.url) ? { url: str(s.url) } : {}),
        ...(num(s.width) > 0 ? { width: Math.round(num(s.width)) } : {}),
        ...(num(s.height) > 0 ? { height: Math.round(num(s.height)) } : {}),
      },
      tint: str(v.tint) || null,
      ...(v.flipH ? { flipH: true } : {}),
      ...(v.flipV ? { flipV: true } : {}),
    }
  }
  const shape = (['rect', 'ellipse', 'line', 'polygon'] as const).find((s) => s === v.shape) ?? 'rect'
  return {
    ...base, type: 'shape',
    shape,
    ...(shape === 'polygon' ? { sides: Math.max(3, Math.min(12, Math.round(num(v.sides, 6)) || 6)) } : {}),
    fill: color(v.fill, '#FFFFFF'),
    stroke: normalizeStroke(v.stroke),
    cornerRadiusMm: Math.max(0, num(v.cornerRadiusMm)),
    ...(v.dashed ? { dashed: true } : {}),
  }
}

/** Tolerante Normalisierung: unbekannte Layer werden verworfen, Werte in gültige Bereiche gezogen. */
export function normalizeDocument(raw: unknown): DesignDocument {
  const d = isObj(raw) ? raw : {}
  const layersRaw = Array.isArray(d.layers) ? d.layers : []
  const seen = new Set<string>()
  const layers: Layer[] = []
  layersRaw.forEach((l, i) => {
    const layer = normalizeLayer(l, i)
    if (!layer) return
    let id = layer.id
    while (seen.has(id)) id = `${id}_${i}`
    seen.add(id)
    layers.push({ ...layer, id })
  })
  const meta = isObj(d.meta) ? d.meta : {}
  return {
    schemaVersion: DESIGN_SCHEMA_VERSION,
    canvas: normalizeCanvas(d.canvas),
    layers,
    meta: {
      templateId: str(meta.templateId) || null,
      templateVersion: num(meta.templateVersion) > 0 ? Math.round(num(meta.templateVersion)) : null,
      generator: str(meta.generator) || 'pfe-design-renderer',
      ...(meta.kind === 'sheet' ? { kind: 'sheet' as const } : {}),
      ...(isObj(meta.sheet) && str(meta.sheet.templateId)
        ? { sheet: { templateId: str(meta.sheet.templateId), ...(str(meta.sheet.name) ? { name: str(meta.sheet.name) } : {}), ...(str(meta.sheet.legacyName) ? { legacyName: str(meta.sheet.legacyName) } : {}) } }
        : {}),
      ...(Array.isArray(meta.productionMarks) ? { productionMarks: meta.productionMarks.filter(isObj) } : {}),
    },
  }
}

/**
 * Strukturelle Validierung eines (rohen) Dokuments. Fehler => ablehnen. Für Warnungen
 * (Auflösung, Beschnitt, leere Slots) siehe warnings.ts.
 */
export function validateDocument(raw: unknown, limits?: { maxLayers?: number; maxTextLayers?: number; maxImageLayers?: number }): ValidationResult {
  const errors: ValidationError[] = []
  const push = (path: string, code: string, message: string) => errors.push({ path, code, message })

  if (!isObj(raw)) {
    push('$', 'NOT_OBJECT', 'Dokument muss ein Objekt sein')
    return { ok: false, errors }
  }
  const version = Number(raw.schemaVersion)
  if (version !== DESIGN_SCHEMA_VERSION) push('$.schemaVersion', 'SCHEMA_VERSION', `schemaVersion ${DESIGN_SCHEMA_VERSION} erwartet`)

  const canvas = isObj(raw.canvas) ? raw.canvas : null
  if (!canvas) push('$.canvas', 'CANVAS_MISSING', 'canvas fehlt')
  else {
    if (!(num(canvas.widthMm) > 0) || !(num(canvas.heightMm) > 0)) push('$.canvas', 'CANVAS_SIZE', 'widthMm/heightMm müssen > 0 sein')
    if (num(canvas.bleedMm) < 0 || num(canvas.safeMarginMm) < 0) push('$.canvas', 'CANVAS_NEGATIVE', 'bleedMm/safeMarginMm dürfen nicht negativ sein')
  }

  const layers = Array.isArray(raw.layers) ? raw.layers : null
  if (!layers) push('$.layers', 'LAYERS_MISSING', 'layers muss ein Array sein')
  else {
    if (limits?.maxLayers && layers.length > limits.maxLayers) push('$.layers', 'TOO_MANY_LAYERS', `maximal ${limits.maxLayers} Ebenen`)
    const ids = new Set<string>()
    let textCount = 0
    let imageCount = 0
    layers.forEach((l, i) => {
      const p = `$.layers[${i}]`
      if (!isObj(l)) { push(p, 'LAYER_NOT_OBJECT', 'Ebene muss ein Objekt sein'); return }
      const type = str(l.type)
      if (!LAYER_TYPES.has(type)) push(`${p}.type`, 'LAYER_TYPE', `unbekannter Typ "${type}"`)
      const id = str(l.id)
      if (!id) push(`${p}.id`, 'LAYER_ID', 'id fehlt')
      else if (ids.has(id)) push(`${p}.id`, 'LAYER_ID_DUPLICATE', `id "${id}" doppelt`)
      ids.add(id)
      const f = isObj(l.frame) ? l.frame : null
      if (!f || !(num(f.wMm) > 0) || !(num(f.hMm) > 0)) push(`${p}.frame`, 'FRAME', 'frame.wMm/hMm müssen > 0 sein')
      if (type === 'text') {
        textCount += 1
        const font = isObj(l.font) ? l.font : null
        if (!font || !str(font.familyId)) push(`${p}.font.familyId`, 'FONT_FAMILY', 'font.familyId fehlt')
      }
      if (type === 'image') imageCount += 1
      if (type === 'sticker' && !(isObj(l.source) && str(l.source.assetId))) push(`${p}.source.assetId`, 'STICKER_ASSET', 'source.assetId fehlt')
    })
    if (limits?.maxTextLayers && textCount > limits.maxTextLayers) push('$.layers', 'TOO_MANY_TEXT_LAYERS', `maximal ${limits.maxTextLayers} Textebenen`)
    if (limits?.maxImageLayers && imageCount > limits.maxImageLayers) push('$.layers', 'TOO_MANY_IMAGE_LAYERS', `maximal ${limits.maxImageLayers} Bildebenen`)
  }

  return { ok: errors.length === 0, errors }
}
